import type { Mark, Node as ProseMirrorNode } from "prosemirror-model";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { EDITOR_MODE } from "../constants";

const markdownToken = (text: string, className = "") => {
  const span = document.createElement("span");
  span.className = `
    editor-v3-markdown-token pointer-events-none select-none font-mono text-muted
    ${className}
  `;
  span.textContent = text;
  return span;
};

const tokenDecorationAttrs = {
  class: "editor-v3-markdown-token",
};

const addTokenDecoration = (
  decorations: Decoration[],
  from: number,
  to: number,
) => {
  if (to <= from) {
    return;
  }

  decorations.push(Decoration.inline(from, to, tokenDecorationAttrs));
};

const rangesOverlap = (
  ranges: Array<[number, number]>,
  from: number,
  to: number,
) => ranges.some(([rangeFrom, rangeTo]) => from < rangeTo && to > rangeFrom);

const addRawTokenPairDecorations = (
  decorations: Decoration[],
  occupiedRanges: Array<[number, number]>,
  text: string,
  pos: number,
  pattern: RegExp,
  closeToken: string,
) => {
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    const openToken = match[1];

    if (!openToken) {
      continue;
    }

    const openFrom = pos + index;
    const openTo = openFrom + openToken.length;
    const closeTo = pos + index + match[0].length;
    const closeFrom = closeTo - closeToken.length;

    if (
      rangesOverlap(occupiedRanges, openFrom, openTo) ||
      rangesOverlap(occupiedRanges, closeFrom, closeTo)
    ) {
      continue;
    }

    addTokenDecoration(decorations, openFrom, openTo);
    addTokenDecoration(decorations, closeFrom, closeTo);
    occupiedRanges.push([openFrom, openTo], [closeFrom, closeTo]);
  }
};

const addRawLinkDecorations = (
  decorations: Decoration[],
  occupiedRanges: Array<[number, number]>,
  text: string,
  pos: number,
) => {
  const linkPattern = /(!?)\[([^\]\n]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;

  for (const match of text.matchAll(linkPattern)) {
    if (match[1]) {
      continue;
    }

    const index = match.index ?? 0;
    const label = match[2] ?? "";
    const url = match[3] ?? "";
    const title = match[4] ?? "";
    const openFrom = pos + index;
    const openTo = openFrom + 1;
    const metadataFrom = openTo + label.length;
    const metadataTo = metadataFrom + 2 + url.length;
    const closeTo = pos + index + match[0].length;
    const closeFrom = title ? closeTo - 2 : closeTo - 1;

    if (
      rangesOverlap(occupiedRanges, openFrom, openTo) ||
      rangesOverlap(occupiedRanges, metadataFrom, closeTo)
    ) {
      continue;
    }

    addTokenDecoration(decorations, openFrom, openTo);
    addTokenDecoration(decorations, metadataFrom, metadataTo);

    if (title) {
      addTokenDecoration(decorations, metadataTo, closeTo);
    } else {
      addTokenDecoration(decorations, closeFrom, closeTo);
    }

    occupiedRanges.push([openFrom, openTo], [metadataFrom, closeTo]);
  }
};

const addRawBlockTokenDecorations = (
  decorations: Decoration[],
  text: string,
  pos: number,
) => {
  const blockToken = /^(#{1,6}\s|>\s|- \[[ xX]\]\s|[-+*]\s|\d+\.\s)/.exec(
    text,
  )?.[0];

  if (blockToken) {
    addTokenDecoration(decorations, pos, pos + blockToken.length);
  }
};

const addRawMarkdownDecorations = (
  decorations: Decoration[],
  node: ProseMirrorNode,
  pos: number,
) => {
  if (!node.isText || !node.text) {
    return;
  }

  addRawBlockTokenDecorations(decorations, node.text, pos);

  const occupiedRanges: Array<[number, number]> = [];

  addRawLinkDecorations(decorations, occupiedRanges, node.text, pos);
  addRawTokenPairDecorations(
    decorations,
    occupiedRanges,
    node.text,
    pos,
    /(\*\*)[^\n*]+?\*\*/g,
    "**",
  );
  addRawTokenPairDecorations(
    decorations,
    occupiedRanges,
    node.text,
    pos,
    /(__)[^\n_]+?__/g,
    "__",
  );
  addRawTokenPairDecorations(
    decorations,
    occupiedRanges,
    node.text,
    pos,
    /(~~)[^\n~]+?~~/g,
    "~~",
  );
  addRawTokenPairDecorations(
    decorations,
    occupiedRanges,
    node.text,
    pos,
    /(`)[^\n`]+?`/g,
    "`",
  );
  addRawTokenPairDecorations(
    decorations,
    occupiedRanges,
    node.text,
    pos,
    /(\*)[^\n*]+?\*/g,
    "*",
  );
  addRawTokenPairDecorations(
    decorations,
    occupiedRanges,
    node.text,
    pos,
    /(_)[^\n_]+?_/g,
    "_",
  );
};

const blockLabel = (name: string, attrs: Record<string, unknown>) => {
  if (name === "heading") {
    return `${"#".repeat(Number(attrs.level) || 1)} `;
  }

  if (name === "blockquote") {
    return "> ";
  }

  if (name === "code_block") {
    return `\`\`\`${attrs.language || ""}`;
  }

  return null;
};

const listItemLabel = (
  node: ProseMirrorNode,
  parent: ProseMirrorNode | null,
  index: number,
) => {
  if (node.type.name !== "list_item" || !parent) {
    return null;
  }

  if (parent.type.name === "bullet_list") {
    if (typeof node.attrs.checked === "boolean") {
      return `- [${node.attrs.checked ? "x" : " "}] `;
    }

    return "- ";
  }

  if (parent.type.name === "ordered_list") {
    return `${(Number(parent.attrs.order) || 1) + index}. `;
  }

  return null;
};

const listItemLabelPosition = (node: ProseMirrorNode, pos: number) => {
  if (node.firstChild?.type.name === "paragraph") {
    return pos + 2;
  }

  return pos + 1;
};

const getMarkTokens = (mark: Mark) => {
  if (mark.type.name === "strong") {
    return { after: "**", before: "**" };
  }

  if (mark.type.name === "em") {
    return { after: "*", before: "*" };
  }

  if (mark.type.name === "strike") {
    return { after: "~~", before: "~~" };
  }

  if (mark.type.name === "code") {
    return { after: "`", before: "`" };
  }

  if (mark.type.name === "link") {
    return {
      after: `](${mark.attrs.href || ""})`,
      before: "[",
    };
  }

  return null;
};

const addInlineDecorations = (
  decorations: Decoration[],
  node: ProseMirrorNode,
  pos: number,
) => {
  if (node.isText) {
    for (const mark of node.marks) {
      const tokens = getMarkTokens(mark);

      if (!tokens) {
        continue;
      }

      decorations.push(
        Decoration.widget(pos, () => markdownToken(tokens.before), {
          relaxedSide: true,
          side: -1,
        }),
        Decoration.widget(
          pos + node.nodeSize,
          () => markdownToken(tokens.after),
          {
            relaxedSide: true,
            side: 1,
          },
        ),
      );
    }

    return;
  }

  if (node.type.name !== "image") {
    return;
  }

  decorations.push(
    Decoration.widget(pos, () => markdownToken(`![${node.attrs.alt || ""}](`), {
      relaxedSide: true,
      side: -1,
    }),
    Decoration.widget(pos + node.nodeSize, () => markdownToken(")"), {
      relaxedSide: true,
      side: 1,
    }),
  );
};

export const markdownModePlugin = (mode: EDITOR_MODE) =>
  new Plugin({
    props: {
      attributes: {
        "data-editor-v3-mode": mode,
      },
      decorations(state) {
        if (mode !== EDITOR_MODE.SIMPLE) {
          return DecorationSet.empty;
        }

        const decorations: Decoration[] = [];

        state.doc.descendants((node, pos, parent, index) => {
          addRawMarkdownDecorations(decorations, node, pos);
          addInlineDecorations(decorations, node, pos);

          const label = blockLabel(node.type.name, node.attrs);

          if (label) {
            decorations.push(
              Decoration.widget(
                pos + 1,
                () => {
                  return markdownToken(label, "pr-1");
                },
                { relaxedSide: true, side: -1 },
              ),
            );
          }

          if (node.type.name === "code_block") {
            decorations.push(
              Decoration.widget(
                pos + node.nodeSize - 1,
                () => {
                  return markdownToken("\n```", "pl-1");
                },
                { relaxedSide: true, side: 1 },
              ),
            );
          }

          const listLabel = listItemLabel(node, parent, index);

          if (listLabel) {
            decorations.push(
              Decoration.widget(
                listItemLabelPosition(node, pos),
                () => markdownToken(listLabel, "pr-1"),
                { relaxedSide: true, side: -1 },
              ),
            );
          }
        });

        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
